import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { flows, sms } from '@repletoryreach/sdk-api';
import { Stack, Group, Button, Text, Skeleton, ActionIcon } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Drawer, Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { Banner, StatusPill, BackIcon } from '../../../components/panel';
import { Textarea } from '@repletoryreach/sdk-ui/inputs';
import {
  FlowCanvas,
  NodeInspector,
  type CanvasPoint,
  type FlowNodeData,
  type FlowViewport
} from '@repletoryreach/sdk-ui/dnd';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { useUnsavedGuard } from '../../../lib/useUnsavedGuard';
import { flowErrorMessage } from '../../../lib/flow-limit';
import { HelpButton } from '../../../app/guides/HelpButton';
import { useT } from '../../../i18n/I18nProvider';
import {
  flowToGraph,
  graphToFlow,
  isPlainObject,
  isEngineNodeKind,
  TRIGGER_NODE_ID,
  TRIGGER_NODE_TYPE,
  type BuilderGraph,
  type BuilderNode,
  type FlowDefinition,
  type JsonObject
} from './mapper';
import {
  appendNode,
  connect,
  duplicateNode,
  hasTrigger,
  knownVariables,
  newNodeId,
  relayout,
  removeEdge,
  insertNode,
  detachNode,
  linksOf,
  removeNode,
  stepNumbers,
  unlinkedNodeIds,
  updateNodeData,
  validateGraph,
  withNodes,
  type ConnectError,
  type PaletteKind
} from './graph-ops';
import { buildPalette, decorateOutputs, makeNodeRenderer, kindTitle, nodeName, portLabel, type NodeActions, type Translate } from './components/nodes';
import { outputsFor } from './components/catalog';
import { buildInspectors, type Inspector } from './components/inspectors';
import { activeFields, useAttributeSuggestions, useContactFieldDefinitions } from '../../../lib/attributes';
import { TestPanel } from './components/TestPanel';
import { ChannelsPanel } from './components/ChannelsPanel';
import { AssistantPanel } from './components/AssistantPanel';
import { TranslationsPanel } from './components/TranslationsPanel';
import { NodePicker } from './components/NodePicker';
import { FlowsDrawer } from './components/FlowsDrawer';
import { FlowDrawer } from '../index';
import { QuickAdd } from './components/QuickAdd';
import { useEmailStatus } from '../../../lib/email';
import { useBotChannels } from './useBotChannels';
import './builder.css';

/**
 * Where the selected step comes from and where it goes, each with a way to
 * remove that link. The line on the canvas has its own × as well; this is
 * the place that names the other end.
 */
function LinksSection({ graph, nodeId, t, onRemove }: { graph: BuilderGraph; nodeId: string; t: Translate; onRemove: (edgeId: string) => void }) {
  const { incoming, outgoing } = linksOf(graph, nodeId);
  const numbers = stepNumbers(graph);
  const describe = (id: string) => {
    const other = graph.nodes.find((n) => n.id === id);
    if (!other) return id;
    const name = nodeName(other) || kindTitle(other.type, t, (other.data ?? {}) as never);
    const n = numbers.get(id);
    return other.id === TRIGGER_NODE_ID || n === undefined ? name : `${t('flows.builder.stepNumber', { n })} · ${name}`;
  };
  // The exit is named as the step names it (a Decision path's name, an option's title), not by its id.
  const via = (source: string, handle: string | undefined) => {
    if (!handle) return '';
    const from = graph.nodes.find((n) => n.id === source);
    const port = (from ? outputsFor(from) : []).find((p) => p.id === handle) ?? { id: handle, label: handle };
    return ` (${t('flows.builder.linkVia', { port: portLabel(port, t, from?.type) })})`;
  };
  return (
    <div className="rr-builder-fs__links">
      <div className="rr-builder-fs__links-head">
        <Text role="caption" fw={700}>{t('flows.builder.links')}</Text>
        <Tooltip label={t('flows.builder.linksHint')} position="left">
          <span className="rr-builder-fs__links-info" aria-label={t('flows.builder.linksHint')}>?</span>
        </Tooltip>
      </div>
      {incoming.length === 0 && outgoing.length === 0 && <Text role="caption" c={MUTED}>{t('flows.builder.noLinks')}</Text>}
      {incoming.map(({ edge, other }) => (
        <div key={edge.id} className="rr-builder-fs__link">
          <span className="rr-builder-fs__link-dir" aria-hidden>←</span>
          <span className="rr-builder-fs__link-text" title={describe(other)}>{t('flows.builder.linkFrom', { step: describe(other) })}{via(other, edge.sourceHandle)}</span>
          <Tooltip label={t('flows.builder.removeLink')} position="left">
            <ActionIcon variant="subtle" size="sm" color="red" aria-label={t('flows.builder.removeLink')} onClick={() => onRemove(edge.id)}>×</ActionIcon>
          </Tooltip>
        </div>
      ))}
      {outgoing.map(({ edge, other }) => (
        <div key={edge.id} className="rr-builder-fs__link">
          <span className="rr-builder-fs__link-dir" aria-hidden>→</span>
          <span className="rr-builder-fs__link-text" title={describe(other)}>{t('flows.builder.linkTo', { step: describe(other) })}{via(nodeId, edge.sourceHandle)}</span>
          <Tooltip label={t('flows.builder.removeLink')} position="left">
            <ActionIcon variant="subtle" size="sm" color="red" aria-label={t('flows.builder.removeLink')} onClick={() => onRemove(edge.id)}>×</ActionIcon>
          </Tooltip>
        </div>
      ))}
    </div>
  );
}

function PencilIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}


type GraphHistory = { graph: BuilderGraph | null; past: BuilderGraph[]; future: BuilderGraph[] };
const MUTED = 'var(--rr-color-text-muted, #94a3b8)';

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (isPlainObject(value)) {
    const out: JsonObject = {};
    for (const k of Object.keys(value).sort()) out[k] = sortKeys(value[k]);
    return out;
  }
  return value;
}
/** Key-order-insensitive fingerprint for the dirty check. */
const fingerprint = (value: unknown): string => JSON.stringify(sortKeys(value));

function isPaletteKind(kind: unknown): kind is PaletteKind {
  return typeof kind === 'string' && (kind === TRIGGER_NODE_TYPE || isEngineNodeKind(kind));
}

function connectMessage(error: ConnectError, t: Translate): string {
  switch (error) {
    case 'self': return t('flows.builder.connect.self');
    case 'into-trigger': return t('flows.builder.connect.intoTrigger');
    case 'cycle': return t('flows.builder.connect.cycle');
    case 'unknown-port': return t('flows.builder.connect.unknownPort');
    default: return t('flows.builder.connect.unknown');
  }
}

/**
 * The visual flow builder: a chatbot as a graph of nodes.
 *
 * `flowToGraph` / `graphToFlow` (pure, lossless) sit between the server
 * definition and sdk-ui's `<FlowCanvas>`; Save PUTs the definition through
 * sdk-api `flows.updateFlow`. The palette on the left, the properties of the
 * selected node on the right, the test chat in a drawer, and the raw JSON
 * behind the Advanced tab.
 */
export default function FlowBuilderPage() {
  const { id } = useParams();
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['flow', id],
    queryFn: () => flows.getFlow(id!),
    enabled: !!id
  });
  const flow = data?.data ?? null;

  /*
   * The graph and its history are one value, so undo and redo are each a
   * single plain state update. Keeping them as three states meant updating
   * one from inside another's updater, which React does not run exactly once
   * or in order (development runs updaters twice on purpose): undo happened to
   * survive, redo never did.
   */
  const [history, setHistory] = useState<GraphHistory>({ graph: null, past: [], future: [] });
  const { graph, past, future } = history;
  const [baseline, setBaseline] = useState('');
  const [hydratedFor, setHydratedFor] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [viewport, setViewport] = useState<FlowViewport>({ x: 0, y: 0, zoom: 1 });
  const [tab, setTab] = useState('canvas');
  const [jsonDraft, setJsonDraft] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [problemsDismissed, setProblemsDismissed] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [flowsOpen, setFlowsOpen] = useState(false);
  const [showProblems, setShowProblems] = useState(true);
  const [testing, setTesting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  // "Build with AI" opens on its own when the flow was created from the list with the assistant.
  const [searchParams, setSearchParams] = useSearchParams();
  const [assisting, setAssisting] = useState(() => searchParams.get('assistant') === '1');
  const canvasHostRef = useRef<HTMLDivElement | null>(null);
  /*
   * Where the quick-add menu opens and what the new node links from: the
   * "+" on a port, a connection dropped on empty canvas, or a double-click.
   */
  const [quickAdd, setQuickAdd] = useState<{
    client: { x: number; y: number };
    after?: { node: string; handle?: string };
    at?: { x: number; y: number };
    /** Put the step on this link (between the two steps it joins). */
    insert?: string;
  } | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // The action bar can stack into two rows on a narrow screen; fit-to-view keeps the nodes under it.
  const actionsRef = useRef<HTMLDivElement>(null);
  const [actionsHeight, setActionsHeight] = useState(64);
  useEffect(() => {
    const el = actionsRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setActionsHeight(el.getBoundingClientRect().height));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  /*
   * A drag is many pointer moves and one undo step. Positions update live
   * (no history), and the graph as it was before the first move is pushed
   * when the drag ends.
   */
  const graphRef = useRef<BuilderGraph | null>(null);
  const dragSnapshot = useRef<BuilderGraph | null>(null);

  // Hydrate once per flow id: server definition → graph.
  useEffect(() => {
    if (!flow || hydratedFor === flow.id) return;
    const def: FlowDefinition = isPlainObject(flow.definition) ? flow.definition : {};
    const graph = flowToGraph(def);
    setHistory({ graph, past: [], future: [] });
    // The baseline is the definition as the builder would write it back, so a
    // graph saved without positions is not "unsaved" the moment it opens.
    setBaseline(fingerprint(graphToFlow(graph)));
    setHydratedFor(flow.id);
    // `?step=` (2 November 2026): a loose end's Open the step lands with that step selected and its properties open.
    const step = searchParams.get('step');
    setSelectedId(step && graph.nodes.some((n) => n.id === step) ? step : null);
  }, [flow, hydratedFor, searchParams]);

  graphRef.current = graph;
  const definition = useMemo(() => (graph ? graphToFlow(graph) : null), [graph]);
  const dirty = definition !== null && fingerprint(definition) !== baseline;
  const unlinked = useMemo(() => new Set(graph ? unlinkedNodeIds(graph) : []), [graph]);
  // USSD (4 October 2026): a flow whose bot answers a USSD code is checked for the steps USSD cannot run.
  const botChannels = useBotChannels(flow?.bot_id);
  // The business's flows (2 November 2026), shared with the Run another flow step's list: a step that opens a deleted
  // or switched-off flow is named. Unchecked until they load.
  const flowList = useQuery({ queryKey: ['flows'], queryFn: () => flows.listFlows(), staleTime: 30_000 });
  const flowRefs = useMemo(
    () => flowList.data?.data.map((f) => ({ id: f.id, name: f.name, is_active: f.is_active })),
    [flowList.data]
  );
  const problems = useMemo(
    () => (graph ? validateGraph(graph, { channels: botChannels, flows: flowRefs }) : []),
    [graph, botChannels, flowRefs]
  );
  const problemsByNode = useMemo(() => {
    const map = new Map<string, 'warning' | 'error'>();
    for (const p of problems) {
      if (!p.nodeId) continue;
      const severity = p.severity ?? 'warning';
      if (severity === 'error' || !map.has(p.nodeId)) map.set(p.nodeId, severity);
    }
    return map;
  }, [problems]);
  const variables = useMemo(() => (graph ? knownVariables(graph) : []), [graph]);

  // The SMS step is offered only while the platform can send SMS. A flow that already has one keeps it;
  // its inspector says SMS is not available yet. Until the answer is in, the step stays out of the list.
  const smsStatus = useQuery({ queryKey: ['sms-status'], queryFn: () => sms.getStatus(), staleTime: 5 * 60_000, retry: false });
  // The email step likewise, while the platform cannot send email (4 October 2026).
  const emailStatus = useEmailStatus();
  const hiddenKinds = useMemo<ReadonlySet<string>>(() => new Set([
    ...(smsStatus.data?.data?.available === true ? [] : ['sms']),
    ...(emailStatus.available ? [] : ['email'])
  ]), [smsStatus.data, emailStatus.available]);
  const palette = useMemo(() => buildPalette(t, hiddenKinds), [t, hiddenKinds]);

  // The form steps on the canvas, for an HTTP step that sends one's answers.
  const formSteps = useMemo(
    () => (graph?.nodes ?? []).filter((n) => n.type === 'form').map((n) => {
      const data = (n.data ?? {}) as Record<string, unknown>;
      return { id: n.id, name: nodeName(n) || kindTitle('form', t, data as never), saveAs: typeof data.save_as === 'string' && data.save_as ? data.save_as : 'form' };
    }),
    [graph?.nodes, t]
  );
  // The contact's fields for `{{` in any message, and the custom field keys
  // the Update contact node may write to. Custom fields are defined under
  // Settings; offering them here is what stops a flow inventing "City" beside
  // an existing "city".
  const contactTokens = useAttributeSuggestions();
  const { fields: fieldDefinitions } = useContactFieldDefinitions();
  const fieldKeys = useMemo(() => activeFields(fieldDefinitions).map((f) => f.key), [fieldDefinitions]);
  const inspectorsByKind = useMemo(
    () => buildInspectors(t, variables, formSteps, { contact: contactTokens, fieldKeys, botId: flow?.bot_id ?? undefined, flowId: flow?.id, flowName: flow?.name }),
    [t, variables, formSteps, contactTokens, fieldKeys, flow?.bot_id, flow?.id, flow?.name]
  );
  const nodeActions = useMemo<NodeActions>(() => ({
    onDuplicate: (nid) => {
      const next = graphRef.current ? newNodeId(graphRef.current) : null;
      apply((g) => duplicateNode(g, nid));
      if (next) setSelectedId(next);
    },
    onDelete: (nid) => { apply((g) => removeNode(g, nid)); setSelectedId(null); },
    onDetach: (nid) => apply((g) => detachNode(g, nid)),
    // The name on the card, typed in place. An empty name clears it.
    onRename: (nid, name) => apply((g) => {
      const node = g.nodes.find((n) => n.id === nid);
      if (!node) return g;
      const data = isPlainObject(node.data) ? node.data : {};
      const rest = Object.fromEntries(Object.entries(data).filter(([k]) => k !== 'name'));
      return updateNodeData(g, nid, name ? { ...rest, name } : rest);
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);
  const stepOf = useMemo(() => (graph ? stepNumbers(graph) : new Map<string, number>()), [graph]);
  const renderNode = useMemo(
    () => makeNodeRenderer(unlinked, problemsByNode, t, nodeActions, stepOf),
    [unlinked, problemsByNode, t, nodeActions, stepOf]
  );
  // What the Connect panel says about how the flow starts.
  const triggerInfo = useMemo(() => {
    const trig = graph?.nodes.find((n) => n.id === TRIGGER_NODE_ID);
    const d = trig && isPlainObject(trig.data) ? trig.data : {};
    const keywords = Array.isArray(d.keywords) ? d.keywords.map(String).filter(Boolean) : [];
    return { type: typeof d.type === 'string' ? d.type : 'keyword', keywords };
  }, [graph]);

  // The canvas draws output ports from `outputs`; the graph keeps them derived.
  const canvasNodes = useMemo<FlowNodeData[]>(
    () => (graph?.nodes ?? []).map((n) => ({ ...n, outputs: decorateOutputs(n, t) })),
    [graph?.nodes, t]
  );
  // A branch pill says what the port under the card says ("Not sure", not "unsure").
  const canvasEdges = useMemo(
    () => (graph?.edges ?? []).map((e) => {
      if (!e.sourceHandle) return e;
      const out = canvasNodes.find((n) => n.id === e.source)?.outputs?.find((o) => o.id === e.sourceHandle);
      return out?.label ? { ...e, label: out.label } : e;
    }),
    [graph?.edges, canvasNodes]
  );

  const nodeTypes = useMemo(() => {
    const map: Record<string, typeof renderNode> = {};
    for (const n of graph?.nodes ?? []) map[n.type] = renderNode;
    return map;
  }, [graph?.nodes, renderNode]);

  const inspectors = useMemo(() => {
    const map: Record<string, Inspector> = { ...inspectorsByKind };
    for (const n of graph?.nodes ?? []) if (!map[n.type]) map[n.type] = inspectorsByKind.__generic;
    return map;
  }, [graph?.nodes, inspectorsByKind]);

  const selectedNode = graph?.nodes.find((n) => n.id === selectedId) ?? null;

  useUnsavedGuard(dirty, {
    title: t('flows.builder.leave.title'),
    body: t('flows.builder.leave.body'),
    discard: t('flows.builder.leave.discard'),
    keep: t('flows.builder.leave.keep')
  });

  const save = useMutation({
    mutationFn: () => flows.updateFlow(id!, { definition: definition ?? {} }),
    onSuccess: (res) => {
      const saved = isPlainObject(res.data?.definition) ? res.data.definition : (definition ?? {});
      setBaseline(fingerprint(saved));
      qc.invalidateQueries({ queryKey: ['flow', id] });
      qc.invalidateQueries({ queryKey: ['flows'] });
      notifySuccess(t('flows.builder.saved'), t('flows.title'));
    },
    // A save that would make the flow count on a full plan (a greeting turned into a keyword flow) says the plan's sentence.
    onError: (err) => notifyError(flowErrorMessage(err, t), t('flows.builder.saveFailed'))
  });
  const { mutate: runSave, isPending: saving } = save;

  /** Every structural change goes through here, so every one is undoable. */
  const apply = useCallback((updater: (g: BuilderGraph) => BuilderGraph) => {
    setHistory((h) => {
      if (!h.graph) return h;
      const next = updater(h.graph);
      if (next === h.graph) return h;
      return { graph: next, past: [...h.past.slice(-49), h.graph], future: [] };
    });
    setProblemsDismissed(false);
  }, []);

  const undo = useCallback(() => {
    setHistory((h) => {
      if (!h.graph || h.past.length === 0) return h;
      return { graph: h.past[h.past.length - 1], past: h.past.slice(0, -1), future: [h.graph, ...h.future.slice(0, 49)] };
    });
    setSelectedId(null);
  }, []);

  const redo = useCallback(() => {
    setHistory((h) => {
      if (!h.graph || h.future.length === 0) return h;
      return { graph: h.future[0], past: [...h.past.slice(-49), h.graph], future: h.future.slice(1) };
    });
    setSelectedId(null);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const key = e.key.toLowerCase();
      if (key === 's') {
        e.preventDefault();
        if (dirty && !saving) runSave();
      } else if (key === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dirty, saving, runSave, undo, redo]);

  /** Positions during a drag: applied straight to the graph, remembered for undo once. */
  const liveNodesChange = useCallback((next: FlowNodeData[]) => {
    setHistory((h) => {
      if (!h.graph) return h;
      if (!dragSnapshot.current) dragSnapshot.current = h.graph;
      return {
        ...h,
        graph: withNodes(h.graph, next.map((n) => {
          const { outputs: _outputs, ...rest } = n;
          return rest as BuilderNode;
        }))
      };
    });
  }, []);
  const commitDrag = useCallback(() => {
    const before = dragSnapshot.current;
    dragSnapshot.current = null;
    if (!before) return;
    setHistory((h) => ({ ...h, past: [...h.past.slice(-49), before], future: [] }));
  }, []);

  /** The quick-add menu chose a kind: add it where the menu was asked for, linked from the port that asked. */
  const quickAddPick = useCallback((kind: string) => {
    const req = quickAdd;
    setQuickAdd(null);
    if (!req || !isPaletteKind(kind) || !graphRef.current) return;
    const id = kind === TRIGGER_NODE_TYPE ? TRIGGER_NODE_ID : newNodeId(graphRef.current);
    const edgeId = req.insert;
    apply((g) => (edgeId ? insertNode(g, kind, edgeId) : appendNode(g, kind, undefined, {
      ...(req.at ? { at: req.at } : {}),
      ...(req.after ? { after: req.after } : {})
    })));
    setSelectedId(id);
  }, [quickAdd, apply]);

  /** Add a node: dropped at a point, or hung off the selected node's free port, or the chain's tail. */
  const addNode = useCallback((kind: PaletteKind, at?: { x: number; y: number }) => {
    // The id the new node will get, known up front so it can be selected once added.
    const newId = kind === TRIGGER_NODE_TYPE ? TRIGGER_NODE_ID : graphRef.current ? newNodeId(graphRef.current) : null;
    apply((g) => {
      const after = !at && selectedId && selectedId !== TRIGGER_NODE_ID
        ? { node: selectedId }
        : undefined;
      const next = appendNode(g, kind, undefined, { ...(at ? { at } : {}), ...(after ? { after } : {}) });
      return next;
    });
    // Select what was just added so its properties are right there.
    if (newId) setSelectedId(newId);
  }, [apply, selectedId]);

  function onConnect({ source, target, sourceHandle }: { source: string; target: string; sourceHandle?: string }) {
    if (!graph) return;
    const res = connect(graph, source, target, sourceHandle);
    if (res.error) {
      setConnectError(connectMessage(res.error, t));
      window.setTimeout(() => setConnectError(null), 4000);
      return;
    }
    apply(() => res.graph);
  }

  function openAdvanced() {
    setJsonDraft(JSON.stringify(definition ?? {}, null, 2));
    setJsonError(null);
  }

  function applyJson() {
    try {
      const parsed: unknown = JSON.parse(jsonDraft);
      if (!isPlainObject(parsed)) {
        setJsonError(t('flows.builder.jsonObjectRequired'));
        return;
      }
      apply(() => flowToGraph(parsed));
      setSelectedId(null);
      setJsonError(null);
      notifySuccess(t('flows.builder.jsonApplied'), t('flows.builder.title'));
    } catch {
      setJsonError(t('flows.builder.invalidJson'));
    }
  }

  function discard() {
    if (!flow) return;
    openConfirmModal({
      title: t('flows.builder.discard.title'),
      children: <Text role="bodySmall">{t('flows.builder.discard.body')}</Text>,
      labels: { confirm: t('flows.builder.discard.confirm'), cancel: t('flows.builder.discard.keep') },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        const def: FlowDefinition = isPlainObject(flow.definition) ? flow.definition : {};
        setHistory({ graph: flowToGraph(def), past: [], future: [] });
        setSelectedId(null);
        if (tab === 'advanced') setJsonDraft(JSON.stringify(def, null, 2));
      }
    });
  }

  if (isLoading || (flow && !graph)) return <Skeleton height={420} />;
  if (error) return <Alert color="error">{(error as Error).message}</Alert>;
  if (!flow || !graph) return <Alert color="warning">{t('flows.builder.flowNotFound')}</Alert>;

  const jsonDirty = jsonDraft !== JSON.stringify(definition ?? {}, null, 2);

  function switchTab(next: string) {
    if (tab === 'advanced' && next !== 'advanced' && jsonDirty) {
      openConfirmModal({
        title: t('flows.builder.json.discard.title'),
        children: <Text role="bodySmall">{t('flows.builder.json.discard.body')}</Text>,
        labels: { confirm: t('flows.builder.json.discard.confirm'), cancel: t('flows.builder.json.discard.keep') },
        confirmProps: { color: 'red' },
        onConfirm: () => setTab(next)
      });
      return;
    }
    setTab(next);
    if (next === 'advanced') openAdvanced();
  }

  const errorCount = problems.filter((p) => p.severity === 'error').length;

  return (
    <div className="rr-builder-fs" style={{ ['--rr-actions-h' as string]: `${actionsHeight}px` }}>
      {/* The canvas is the page. */}
      <div className="rr-builder-fs__canvas" ref={canvasHostRef}>
        {connectError && (
          <div className="rr-builder__connect-error" role="status">{connectError}</div>
        )}
        {graph.nodes.filter((n) => n.id !== TRIGGER_NODE_ID).length === 0 && (
          <div className="rr-builder__canvas-empty" aria-hidden>
            <span>{t('flows.builder.canvasEmpty')}</span>
          </div>
        )}
        <FlowCanvas
          nodes={canvasNodes}
          edges={canvasEdges}
          orientation="vertical"
          fitViewOnInit
          fitInsets={{ top: actionsHeight + 16, bottom: problems.length > 0 && showProblems && !problemsDismissed ? 150 : 56, left: 130, right: 56 }}
          onNodesChange={liveNodesChange}
          onNodeDragEnd={commitDrag}
          nodeTypes={nodeTypes}
          selectedNodeId={selectedId}
          onSelectedNodeChange={setSelectedId}
          viewport={viewport}
          onViewportChange={setViewport}
          onConnect={onConnect}
          onConnectEnd={(source, handle, point: CanvasPoint) => setQuickAdd({ client: point.client, after: { node: source, handle }, at: point.flow })}
          onAddFrom={(source, handle, anchor) => setQuickAdd({ client: { x: anchor.clientX, y: anchor.clientY }, after: { node: source, handle } })}
          onCanvasDoubleClick={(point: CanvasPoint) => setQuickAdd({ client: point.client, at: point.flow })}
          onDeleteNode={(nid) => { apply((g) => removeNode(g, nid)); setSelectedId(null); }}
          onDeleteEdge={(eid) => apply((g) => removeEdge(g, eid))}
          onInsertEdge={(eid, a) => setQuickAdd({ client: { x: a.clientX, y: a.clientY }, insert: eid })}
          aria-label={t('flows.builder.canvas')}
          labels={{
            zoomIn: t('flows.builder.canvasControls.zoomIn'),
            zoomOut: t('flows.builder.canvasControls.zoomOut'),
            reset: t('flows.builder.canvasControls.reset'),
            fit: t('flows.builder.canvasControls.fit'),
            minimap: t('flows.builder.canvasControls.overview'),
            addFrom: t('flows.builder.canvasControls.addHere'),
            removeEdge: t('flows.builder.canvasControls.removeLink'),
            insertOn: t('flows.builder.canvasControls.insertHere')
          }}
        />
      </div>

      {/* Who this flow is. */}
      <div className="rr-builder-fs__bar rr-builder-fs__bar--tl" data-guide="builder-identity">
        <Tooltip label={t('flows.builder.back')} position="bottom">
          <ActionIcon variant="subtle" aria-label={t('flows.builder.back')} onClick={() => nav(`/flows/${flow.id}`)}><BackIcon /></ActionIcon>
        </Tooltip>
        <div className="rr-builder-fs__identity">
          <div className="rr-builder-fs__title">
            <h1 title={flow.name}>{flow.name}</h1>
            <Tooltip label={t('flows.builder.flowSettings')} position="bottom">
              <ActionIcon variant="subtle" size="sm" aria-label={t('flows.builder.flowSettings')} onClick={() => setSettingsOpen(true)}><PencilIcon /></ActionIcon>
            </Tooltip>
            <StatusPill tone={flow.is_active ? 'active' : 'paused'}>{flow.is_active ? t('status.active') : t('status.inactive')}</StatusPill>
            {dirty && <StatusPill tone="submitted">{t('flows.builder.unsaved')}</StatusPill>}
          </div>
          <span className="rr-builder-fs__desc" title={flow.description ?? ''}>{flow.description || t('flows.builder.descriptionEmpty')}</span>
        </div>
      </div>

      {/* What you can do. */}
      <div className="rr-builder-fs__bar rr-builder-fs__bar--tr" ref={actionsRef}>
        <div className="rr-builder-fs__group">
          <HelpButton guide="automation" />
          <Button size="sm" variant="default" leftSection={<FlowsIcon />} onClick={() => setFlowsOpen(true)}>{t('flows.builder.flowsButton')}</Button>
          <Button size="sm" data-guide="builder-add" onClick={() => setPickerOpen(true)}>+ {t('flows.builder.addStep')}</Button>
          <Button size="sm" variant="default" data-guide="builder-ai" leftSection={<SparkIcon />} onClick={() => setAssisting(true)}>{t('flows.builder.assistant')}</Button>
          <Button size="sm" variant="default" data-guide="builder-connect" leftSection={<LinkIcon />} onClick={() => setConnecting(true)}>{t('flows.builder.connect')}</Button>
        </div>
        <div className="rr-builder-fs__group">
          <Button size="sm" variant="default" data-guide="builder-test" leftSection={<PlayIcon />} onClick={() => setTesting(true)}>{t('flows.builder.test')}</Button>
          {/* Translations (1 November 2026): the flow's wording in each other language its chatbot speaks. */}
          <Button size="sm" variant="default" leftSection={<GlobeIcon />} onClick={() => switchTab('translations')} data-testid="builder-translations">
            {t('flows.translations.tab')}
          </Button>
          <Tooltip label={t('flows.builder.jsonDrawer')} position="bottom">
            <Button size="sm" variant="default" onClick={() => switchTab('advanced')}>{t('flows.builder.jsonShort')}</Button>
          </Tooltip>
          {dirty ? <Button size="sm" variant="default" disabled={saving} onClick={discard}>{t('flows.builder.discard')}</Button> : null}
          <Button size="sm" data-guide="builder-save" disabled={!dirty} loading={saving} onClick={() => runSave()}>{t('flows.builder.save')}</Button>
        </div>
      </div>

      {/* Undo and friends. */}
      <div className="rr-builder-fs__tools">
        <Button size="xs" variant="subtle" disabled={past.length === 0} onClick={undo} aria-label={t('flows.builder.undo')}>↺ {t('flows.builder.undo')}</Button>
        <Button size="xs" variant="subtle" disabled={future.length === 0} onClick={redo} aria-label={t('flows.builder.redo')}>↻ {t('flows.builder.redo')}</Button>
        <Button size="xs" variant="subtle" onClick={() => apply((g) => relayout(g))}>{t('flows.builder.tidy')}</Button>
        {selectedNode && selectedNode.id !== TRIGGER_NODE_ID && (
          <Button size="xs" variant="subtle" color="red" onClick={() => { apply((g) => removeNode(g, selectedNode.id)); setSelectedId(null); }}>
            {t('flows.builder.removeNode')}
          </Button>
        )}
        <div className="rr-builder-fs__stat">
          {t('flows.builder.stats', { nodes: graph.nodes.filter((n) => n.id !== TRIGGER_NODE_ID).length, problems: errorCount })}
        </div>
      </div>

      {/* What needs fixing. */}
      {problems.length > 0 && !problemsDismissed && (
        <div className="rr-builder-fs__problems">
          <button type="button" className="rr-builder-fs__problems-toggle" onClick={() => setShowProblems((v) => !v)}>
            {showProblems ? t('flows.builder.problemsHide') : t('flows.builder.problemsToggle', { count: problems.length })}
          </button>
          {showProblems && problems.slice(0, 4).map((p, i) => (
            <Banner
              key={`${p.key}-${p.nodeId ?? ''}-${i}`}
              tone={p.severity === 'error' ? 'warning' : p.key === 'flows.validate.noTrigger' ? 'info' : 'warning'}
              action={p.nodeId ? (
                <button type="button" className="rr-panel__clear" onClick={() => setSelectedId(p.nodeId!)}>
                  {t('flows.builder.showNode')}
                </button>
              ) : undefined}
              onDismiss={i === 0 ? () => setProblemsDismissed(true) : undefined}
              dismissLabel={t('action.dismiss')}
            >
              {t(p.key, p.vars)}
            </Banner>
          ))}
          {showProblems && problems.length > 4 && (
            <Text role="caption" c={MUTED}>{t('flows.builder.moreProblems', { count: problems.length - 4 })}</Text>
          )}
        </div>
      )}

      {/* The selected step's properties. */}
      {selectedNode && (
        <aside className="rr-builder-fs__inspector" aria-label={t('flows.builder.properties')}>
          <div className="rr-builder-fs__inspector-head">
            <div>
              <Text role="caption" c={MUTED}>{kindTitle(selectedNode.type, t)}</Text>
              <Text role="bodySmall" fw={650}>{nodeName(selectedNode) || kindTitle(selectedNode.type, t)}</Text>
            </div>
            <ActionIcon variant="subtle" size="sm" aria-label={t('flows.builder.closeInspector')} onClick={() => setSelectedId(null)}>×</ActionIcon>
          </div>
          {graph && (
            <LinksSection
              graph={graph}
              nodeId={selectedNode.id}
              t={t}
              onRemove={(eid) => apply((g) => removeEdge(g, eid))}
            />
          )}
          <div className="rr-builder-fs__inspector-body">
            {/* Keyed by the step: an inspector is called as a function, so its hooks live here, and a step of
                another kind (with other hooks, such as the Book and Ticket steps' setup reads) needs a fresh one. */}
            <NodeInspector
              key={selectedNode.id}
              node={selectedNode}
              inspectors={inspectors}
              onChange={(d) => { if (selectedId) apply((g) => updateNodeData(g, selectedId, d as JsonObject)); }}
              onDelete={(nid) => { apply((g) => removeNode(g, nid)); setSelectedId(null); }}
              title={t('flows.builder.properties')}
              emptyState={t('flows.builder.selectNode')}
            />
          </div>
        </aside>
      )}

      <NodePicker
        opened={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(kind) => { if (isPaletteKind(kind)) addNode(kind); }}
        onAssistant={() => setAssisting(true)}
        hasTrigger={hasTrigger(graph)}
        hidden={hiddenKinds}
      />

      <Drawer opened={tab === 'advanced'} onClose={() => switchTab('canvas')} title={t('flows.builder.jsonDrawer')} position="right" size="lg">
        <Stack gap="sm">
          <Text role="bodySmall" c={MUTED}>{t('flows.builder.advancedHint')}</Text>
          <Textarea
            className="rr-flow-builder__json"
            value={jsonDraft}
            onChange={(e) => setJsonDraft(e.currentTarget.value)}
            minRows={22}
            error={jsonError ?? undefined}
            aria-label={t('flows.builder.advancedTab')}
          />
          <Group justify="flex-end" gap="xs">
            <Button size="sm" variant="default" disabled={!jsonDirty} onClick={openAdvanced}>{t('action.cancel')}</Button>
            <Button size="sm" disabled={!jsonDirty} onClick={applyJson}>{t('flows.builder.applyJson')}</Button>
          </Group>
        </Stack>
      </Drawer>

      <TranslationsPanel
        opened={tab === 'translations'}
        onClose={() => switchTab('canvas')}
        flowId={flow.id}
        flowName={flow.name}
        botId={flow.bot_id}
        dirty={dirty}
      />

      {settingsOpen ? (
        <FlowDrawer
          flow={flow}
          cloneFrom={null}
          onClose={() => setSettingsOpen(false)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['flows'] }); qc.invalidateQueries({ queryKey: ['flow', flow.id] }); }}
        />
      ) : null}
      <FlowsDrawer opened={flowsOpen} onClose={() => setFlowsOpen(false)} currentId={flow.id} dirty={dirty} />

      <QuickAdd
        anchor={quickAdd?.client ?? null}
        title={t('flows.builder.quickAdd.title')}
        hint={quickAdd?.after || quickAdd?.insert ? t('flows.builder.quickAdd.hint') : undefined}
        searchLabel={t('flows.builder.quickAdd.search')}
        emptyLabel={t('flows.builder.quickAdd.empty')}
        items={palette.filter((p) => p.type !== TRIGGER_NODE_TYPE || !hasTrigger(graph))}
        onPick={quickAddPick}
        onClose={() => setQuickAdd(null)}
      />

      <TestPanel
        opened={testing}
        onClose={() => setTesting(false)}
        flowId={flow.id}
        flowName={flow.name}
        definition={definition}
      />
      <ChannelsPanel
        opened={connecting}
        onClose={() => setConnecting(false)}
        flowId={flow.id}
        flowName={flow.name}
        isActive={!!flow.is_active}
        triggerType={triggerInfo.type}
        keywords={triggerInfo.keywords}
        onTest={() => { setConnecting(false); setTesting(true); }}
      />
      <AssistantPanel
        opened={assisting}
        onClose={() => {
          setAssisting(false);
          if (searchParams.get('assistant')) setSearchParams((sp) => { sp.delete('assistant'); return sp; }, { replace: true });
        }}
        flowId={flow.id}
        flowName={flow.name}
        definition={definition ?? {}}
        onApply={(def) => { apply(() => relayout(flowToGraph(def))); setSelectedId(null); }}
        onUndo={undo}
      />
    </div>
  );
}

function FlowsIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2" y="2" width="12" height="3" rx="1" /><rect x="2" y="6.5" width="12" height="3" rx="1" /><rect x="2" y="11" width="12" height="3" rx="1" />
    </svg>
  );
}

function SparkIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <path d="M8 1.5 9.6 6l4.4 1.6L9.6 9.2 8 13.7 6.4 9.2 2 7.6 6.4 6z" />
      <path d="M13 11.2l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="8" cy="8" r="6" /><path d="M2 8h12M8 2c1.8 1.7 2.6 3.7 2.6 6S9.8 12.3 8 14c-1.8-1.7-2.6-3.7-2.6-6S6.2 3.7 8 2z" />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6.5 9.5a3 3 0 0 0 4.2 0l2-2a3 3 0 0 0-4.2-4.2l-1 1" />
      <path d="M9.5 6.5a3 3 0 0 0-4.2 0l-2 2a3 3 0 0 0 4.2 4.2l1-1" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <path d="M5 3.4v9.2a.6.6 0 0 0 .92.5l7-4.6a.6.6 0 0 0 0-1l-7-4.6A.6.6 0 0 0 5 3.4z" />
    </svg>
  );
}
